/**
 * Utility functions for formatting phone and fax numbers.
 */
import * as libphonenumber from 'google-libphonenumber';

/**
 * Formats a phone or fax number with an optional region code.
 * If both regionCode and number are provided, parses the number and returns
 * "+{countryCode} {nationalNumber}", where the national part uses libphonenumber's
 * NATIONAL format for the given region (e.g. parentheses and dashes for US/CA).
 * Otherwise, returns empty string.
 *
 * @param number - The phone or fax number to format (digits or locally formatted input, e.g. "4155551234" or "(415) 555-1234")
 * @param regionCode - Optional ISO 3166-1 alpha-2 region code (e.g., "US", "CA", "GB")
 * @returns Formatted number string (e.g., "+1 (415) 555-1234"), or empty string if number/regionCode is missing or parsing fails
 *
 * @example
 * formatPhoneOrFaxNumber('4155551234', 'US') // Returns "+1 (415) 555-1234"
 * formatPhoneOrFaxNumber('2012345678', 'GB') // Returns "+44 020 1234 5678"
 * formatPhoneOrFaxNumber('4155551234', null) // Returns ""
 * formatPhoneOrFaxNumber(null, 'US') // Returns ""
 */
export const formatPhoneOrFaxNumber = (
  number?: string | null,
  regionCode?: string | null,
): string => {
  // Return empty string if either is missing
  if (!regionCode?.trim() || !number?.trim()) {
    return '';
  }

  try {
    const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

    // Parse the number with the region code
    const phoneNumber = phoneUtil.parseAndKeepRawInput(number, regionCode);

    // Get country code and region-specific national format
    const countryCode = phoneNumber.getCountryCode();
    const nationalNumber = phoneUtil.format(
      phoneNumber,
      libphonenumber.PhoneNumberFormat.NATIONAL,
    );

    // Format as "+{countryCode} {nationalNumber}"
    return `+${countryCode} ${nationalNumber}`;
  } catch {
    // If parsing fails, return empty string
    return '';
  }
};
